import { useAppDispatch } from "@/store";
import Image from "next/image";
import { selectProduct, setSelectedPrice } from "@/store/slices/product";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./filter.module.scss";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import CurrencyInr from "@/components/common/currency-inr";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";

const getMetalIconSrc = (purity?: string) => {
  switch (purity?.toLowerCase()) {
    case "titanium":
      return require("@/assets/images/metal-titanium.svg");
    case "silver":
      return require("@/assets/images/metal-silver.svg");
    case "platinum":
      return require("@/assets/images/metal-platinum.svg");
    default:
      return require("@/assets/images/metal.svg");
  }
};

const getMetalWeightIconSrc = (purity?: string) => {
  switch (purity?.toLowerCase()) {
    case "titanium":
      return require("@/assets/images/MetalWeight-titanium.svg");
    case "silver":
      return require("@/assets/images/MetalWeight-silver.svg");
    case "platinum":
      return require("@/assets/images/MetalWeight-platinum.svg");
    default:
      return require("@/assets/images/MetalWeight.svg");
  }
};

const ProductDetailsFilter = () => {
  const dispatch = useAppDispatch();
  const productState = useSelector(selectProduct);
  const isStock = useMemo(
    () =>
      productState.data?.prices.find(
        (v) =>
          v.shipping_days <=
            parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "") &&
          v.stock > 0
      ),
    [productState]
  );

  const showMetalWeightlist = (array: any) => {
    let data = array
      .filter(
        (v: any) =>
          v.size === productState.selectedPrice?.size &&
          v.purity === productState.selectedPrice?.purity &&
          v.diamond_shape === productState.selectedPrice?.diamond_shape
      )
      .filter(
        (value: any, index: any, self: any) =>
          index ===
          self.findIndex(
            (item: any) => item.metal_weight === value.metal_weight
          )
      );

    const hasLess = data?.some(
      (p: any) =>
        p.shipping_days <=
        parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "")
    );
    const hasMore = data?.some(
      (p: any) =>
        p.shipping_days >
        parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "")
    );

    if (hasLess && hasMore) {
      data = data.filter(
        (p: any) =>
          p.shipping_days <=
          parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "")
      );
    }

    return data;
  };
  const [selectedComboOptions, setSelectedComboOptions] = useState<any>({
    size: productState.selectedPrice?.size,
    purity: productState.selectedPrice?.purity,
    diamond_shape: productState.selectedPrice?.diamond_shape,
    metal_weight: productState.selectedPrice?.metal_weight,
  });
  const [oldComboOptions, setOldComboOptions] = useState({
    size: productState.selectedPrice?.size,
    purity: productState.selectedPrice?.purity,
    diamond_shape: productState.selectedPrice?.diamond_shape,
    metal_weight: productState.selectedPrice?.metal_weight,
  });

  const changeCombo = (value: any, type: string) => {
    let newOptions = selectedComboOptions;
    let price = productState.data?.prices
      .slice()
      .sort((a, b) => a.shipping_days - b.shipping_days)
      .find(
        (v) =>
          v.purity ==
            (type == "purity" ? value : selectedComboOptions.purity) &&
          v.size == (type == "size" ? value : selectedComboOptions.size) &&
          v.diamond_shape ==
            (type == "diamond_shape"
              ? value
              : selectedComboOptions.diamond_shape) &&
          (type == "metal_weight" ? v.metal_weight == value : true)
      );
    if (!price) {
      price = productState.data?.prices
        .slice()
        .sort((a, b) => a.shipping_days - b.shipping_days)
        .find((v: any) => v[type] == value);
    }

    if (price) {
      dispatch(setSelectedPrice(price));
      setSelectedComboOptions({
        purity: price?.purity,
        size: price?.size,
        diamond_shape: price?.diamond_shape,
        metal_weight: price?.metal_weight,
      });
    }
  };
  const noChange = () => {
    const newPrice = productState.data?.prices.find(
      (v) =>
        v.size == oldComboOptions.size &&
        v.purity == oldComboOptions.purity &&
        v.diamond_shape == oldComboOptions.diamond_shape &&
        v.metal_weight == oldComboOptions.metal_weight
    );
    if (newPrice) {
      dispatch(setSelectedPrice(newPrice));
      setSelectedComboOptions(oldComboOptions);
    }
  };

  const fastDeliveryText = (size: any) => {
    const isFast = productState.data?.prices.find(
      (v) =>
        v.shipping_days <=
          parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "") &&
        v.stock > 0
    );
    if (isFast) {
      const item = productState.data?.prices.find((v) => v.size == size);
      if (item) {
        if (
          item.shipping_days <=
            parseInt(process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "") &&
          item.stock > 0
        ) {
          return "(Ready to Ship)";
        } else {
          return "(Made to Order)";
        }
      }
    }
  };

  if (!productState.data) {
    return <></>;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  return (
    <>
      <div style={{ width: "100%", fontFamily: "'Poppins', sans-serif" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: "15px",
            padding: "5px 0",
            cursor: "pointer",
          }}
          onClick={handleShow}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",

              minWidth: 0,
              gap: "15px",
              overflowX: "auto",
              paddingBottom: "1px", // slightly offset to prevent any visual clipping
            }}
          >
            {!!productState.data.variation.size.length && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "15px",
                  borderRight: "1px solid #eaeaea",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={require("@/assets/images/nav-icon/AllProducts.svg")}
                  width={28}
                  height={28}
                  alt="Size"
                  unoptimized
                  style={{ marginRight: "10px" }}
                />
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "12px", color: "#666" }}>Size</div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    {oldComboOptions?.size}
                  </div>
                </div>
              </div>
            )}
            {!!productState.data.variation.purity?.length && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "15px",
                  borderRight: "1px solid #eaeaea",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={getMetalIconSrc(productState.data?.type || '')}
                  width={28}
                  height={28}
                  alt="Metal"
                  unoptimized
                  style={{ marginRight: "10px" }}
                />
                <div style={{ marginLeft: "5px" }}>
                  <div style={{ fontSize: "12px", color: "#666" }}>Metal</div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    {oldComboOptions?.purity}
                  </div>
                </div>
              </div>
            )}
            {!!productState.data.variation.diamond_shape?.length && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "15px",
                  borderRight: "1px solid #eaeaea",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={require("@/assets/images/nav-icon/Diamond.svg")}
                  width={28}
                  height={28}
                  alt="Diamond"
                  unoptimized
                  style={{ marginRight: "10px" }}
                />
                <div style={{ marginLeft: "10px" }}>
                  <div style={{ fontSize: "12px", color: "#666" }}>Diamond</div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    {oldComboOptions?.diamond_shape}
                  </div>
                </div>
              </div>
            )}
            {!!(
              oldComboOptions?.metal_weight ||
              productState.data?.prices?.[0]?.metal_weight
            ) && (
              <div
                style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                <Image
                  src={getMetalWeightIconSrc(
                      productState.data?.type || ''
                  )}
                  width={28}
                  height={28}
                  alt="Weight"
                  unoptimized
                  style={{ marginRight: "10px" }}
                />
                <div style={{ marginLeft: "5px" }}>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Metal Weight
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    {oldComboOptions?.metal_weight ||
                      productState.data?.prices?.[0]?.metal_weight}{" "}
                    g
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: "15px",
              borderLeft: "1px solid #eaeaea",
              marginLeft: "10px",
              flexShrink: 0,
              backgroundColor: "#fff",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8B0000"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: "8px" }}
            >
              <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
              <polyline points="2 12 12 17 22 12"></polyline>
              <polyline points="2 17 12 22 22 17"></polyline>
            </svg>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "500",
                color: "#8B0000",
                lineHeight: "1.2",
              }}
            >
              Check
              <br />
              Variants
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={show}
        className="right"
        onHide={() => {
          handleClose();
          noChange();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="main-price">
              <div className="small-text">Price</div>
              <div className="large-text">
                <span className="price">
                  {" "}
                  <CurrencyInr
                    value={
                      productState.selectedPrice?.discount
                        ? productState.selectedPrice?.discount.total
                        : productState.selectedPrice?.total
                    }
                  />
                </span>
                {!!productState.selectedPrice?.discount && (
                  <span className="cross-price">
                    <CurrencyInr value={productState.selectedPrice.total} />
                  </span>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* {JSON.stringify(productState.data.variation)}
          {JSON.stringify(productState.data?.prices)} */}
          {!!productState.data.variation.purity?.length && (
            <>
              <div className="d-flex justify-content-between">
                <div className={styles.filter_heading_modal}>Metal Purity</div>
              </div>

              <div className={styles.filter_small_card_container}>
                {productState.data?.prices
                  .filter(
                    (value, index, self) =>
                      index ===
                      self.findIndex((item) => item.purity === value.purity)
                  )
                  .map((v, k) => {
                    const isStock = productState.data?.prices.some(
                      (item) =>
                        item.purity === v.purity &&
                        item.shipping_days <=
                          parseInt(
                            process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "0"
                          )
                    );

                    const selectedMatch =
                      v.purity === productState.selectedPrice?.purity;

                    const selectedCombination = productState.data?.prices
                      .slice()
                      .sort((a, b) => a.shipping_days - b.shipping_days)
                      .find(
                        (x) =>
                          x.metal_weight ===
                            productState.selectedPrice?.metal_weight &&
                          x.size === productState.selectedPrice?.size &&
                          x.diamond_shape ===
                            productState.selectedPrice?.diamond_shape &&
                          x.purity === v.purity
                      );

                    const shippingDays =
                      selectedCombination?.shipping_days || 0;
                    const fastDeliveryDays = parseInt(
                      process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "0"
                    );

                    return (
                      <div
                        key={k}
                        className={`${styles.filter_small_card} ${
                          selectedMatch ? styles.active : ""
                        }`}
                        onClick={() => changeCombo(v.purity, "purity")}
                      >
                        <p className={styles.filter_small_card_heading}>
                          {v.purity}
                        </p>
                        <div className={styles.filter_small_card_body}>
                          {selectedMatch
                            ? shippingDays <= fastDeliveryDays
                              ? "in Stock!"
                              : "made to order"
                            : isStock
                            ? "in Stock!"
                            : "made to order"}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
          {!!productState.data.variation.size.length && (
            <>
              <div className="d-flex justify-content-between">
                <div className={styles.filter_heading_modal}>Select Size</div>
                <div className={styles.link_text}>
                  {!!productState.data.size_chart && (
                    <>
                      <a
                        href={productState.data.size_chart}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#FF1933",
                        }}
                      >
                        {productState.data.size_chart_label}
                      </a>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.filter_small_card_container}>
                {productState.data?.prices
                  .filter(
                    (v) => v.purity === productState.selectedPrice?.purity
                  )
                  .filter(
                    (value, index, self) =>
                      index ===
                      self.findIndex((item) => item.size === value.size)
                  )
                  .map((v, k) => {
                    const isSelectedSize =
                      v.size === productState.selectedPrice?.size;

                    const selectedCombination = productState.data?.prices
                      .slice()
                      .sort((a, b) => a.shipping_days - b.shipping_days)
                      .find(
                        (x) =>
                          x.metal_weight ===
                            productState.selectedPrice?.metal_weight &&
                          x.purity === productState.selectedPrice?.purity &&
                          x.diamond_shape ===
                            productState.selectedPrice?.diamond_shape &&
                          x.size === v.size
                      );

                    const fastDeliveryDays = parseInt(
                      process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "0"
                    );
                    const shippingDays =
                      selectedCombination?.shipping_days || 0;

                    const isStock = productState.data?.prices.some(
                      (item) =>
                        item.purity === productState.selectedPrice?.purity &&
                        item.size === v.size &&
                        item.shipping_days <= fastDeliveryDays
                    );

                    return (
                      <div
                        key={k}
                        className={`${styles.filter_small_card_h} ${
                          isSelectedSize ? styles.active : ""
                        }`}
                        onClick={() => changeCombo(v.size, "size")}
                      >
                        <p className={styles.filter_small_card_heading}>
                          {v.size}
                        </p>
                        <div className={styles.filter_small_card_body}>
                          {isSelectedSize
                            ? shippingDays <= fastDeliveryDays
                              ? "in Stock!"
                              : "made to order"
                            : isStock
                            ? "in Stock!"
                            : "made to order"}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
          {!!productState.data.variation.diamond_shape?.length && (
            <>
              {" "}
              <div className="d-flex justify-content-between">
                <div className={styles.filter_heading_modal}>
                  Diamond Quality
                </div>
              </div>
              <div className={styles.filter_small_card_container}>
                {productState.data?.prices
                  .filter(
                    (v) =>
                      v.size === productState.selectedPrice?.size &&
                      v.purity === productState.selectedPrice?.purity
                  )
                  .filter(
                    (value, index, self) =>
                      index ===
                      self.findIndex(
                        (item) => item.diamond_shape === value.diamond_shape
                      )
                  )
                  ?.map((v, k) => {
                    const isSelectedShape =
                      v.diamond_shape ===
                      productState.selectedPrice?.diamond_shape;

                    const selectedCombination = productState.data?.prices
                      .slice()
                      .sort((a, b) => a.shipping_days - b.shipping_days)
                      .find(
                        (x) =>
                          x.metal_weight ===
                            productState.selectedPrice?.metal_weight &&
                          x.size === productState.selectedPrice?.size &&
                          x.purity === productState.selectedPrice?.purity &&
                          x.diamond_shape === v.diamond_shape
                      );

                    const fastDeliveryDays = parseInt(
                      process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "0"
                    );
                    const shippingDays =
                      selectedCombination?.shipping_days || 0;

                    const isStock = productState.data?.prices.some(
                      (item) =>
                        item.size === productState.selectedPrice?.size &&
                        item.purity === productState.selectedPrice?.purity &&
                        item.diamond_shape === v.diamond_shape &&
                        item.shipping_days <= fastDeliveryDays
                    );

                    return (
                      <div
                        key={k}
                        className={`${styles.filter_small_card} ${
                          isSelectedShape ? styles.active : ""
                        }`}
                        onClick={() =>
                          changeCombo(v.diamond_shape, "diamond_shape")
                        }
                      >
                        <p className={styles.filter_small_card_heading}>
                          {v.diamond_shape}
                        </p>
                        <div className={styles.filter_small_card_body}>
                          {isSelectedShape
                            ? shippingDays <= fastDeliveryDays
                              ? "in Stock!"
                              : "made to order"
                            : isStock
                            ? "in Stock!"
                            : "made to order"}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}{" "}
          <div className="d-flex justify-content-between">
            <div className={styles.filter_heading_modal}>Metal weight</div>
            {/* <div className={styles.link_text}>DIAMOND GUIDE</div> */}
          </div>
          <div className={styles.filter_small_card_container}>
            {showMetalWeightlist(productState.data?.prices).map(
              (v: any, k: any) => {
                const isSelectedWeight =
                  v.metal_weight === productState.selectedPrice?.metal_weight;

                const selectedCombination = productState.data?.prices
                  .slice()
                  .sort((a, b) => a.shipping_days - b.shipping_days)
                  .find(
                    (x) =>
                      x.metal_weight === v.metal_weight &&
                      x.size === productState.selectedPrice?.size &&
                      x.purity === productState.selectedPrice?.purity &&
                      x.diamond_shape ===
                        productState.selectedPrice?.diamond_shape
                  );

                const fastDeliveryDays = parseInt(
                  process.env.NEXT_PUBLIC_FAST_DELIVERY_DAYS || "0"
                );
                const shippingDays = selectedCombination?.shipping_days || 0;

                const isStock = productState.data?.prices.some(
                  (item) =>
                    item.metal_weight === v.metal_weight &&
                    item.size === productState.selectedPrice?.size &&
                    item.purity === productState.selectedPrice?.purity &&
                    item.diamond_shape ===
                      productState.selectedPrice?.diamond_shape &&
                    item.shipping_days <= fastDeliveryDays
                );

                return (
                  <div
                    key={k}
                    className={`${styles.filter_small_card} ${
                      isSelectedWeight ? styles.active : ""
                    }`}
                    onClick={() => changeCombo(v.metal_weight, "metal_weight")}
                  >
                    <p className={styles.filter_small_card_heading}>
                      {v.metal_weight}
                    </p>
                    <div className={styles.filter_small_card_body}>
                      {isSelectedWeight
                        ? shippingDays <= fastDeliveryDays
                          ? "in Stock!"
                          : "made to order"
                        : isStock
                        ? "in Stock!"
                        : "made to order"}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className={styles.button_text}
            onClick={() => {
              handleClose();
              setOldComboOptions(selectedComboOptions);
              if (window.smartech) {
                unbxdCusEngTrackEvent("Product View", {
                  source: "web",
                  platform: "senco",
                  prid: productState.data?.id,
                  variant_id: productState.selectedPrice?.id,
                  image: productState.data?.images[0].media,
                  prqt: 1,
                  product_name: productState.data?.title,
                  amount: productState.data?.prices[0].total,
                  product_url: `${window.location.origin}/products/${productState.data?.slug}`,
                  discounted_price:
                    productState.selectedPrice?.discount?.price || 0,
                  stock_availability: productState.data?.stock ? 1 : 0,
                });
              }
            }}
          >
            CONFIRM
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductDetailsFilter;
